import React, { FC, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ExampleTable,
  ExampleTableCell,
  ExampleTableHead,
  ExampleTableRow,
  GuideBackdrop,
  GuideBody,
  GuideCloseButton,
  GuideFooter,
  GuideHeader,
  GuideKickerLabel,
  GuideKickerRow,
  GuideSurveyBadge,
  GuideModal,
  GuidePrimaryButton,
  GuideSection,
  GuideSubtitle,
  GuideTitle,
  HierarchyCanvas,
  HierarchyChildGroup,
  HierarchyLeaf,
  HierarchyTopGroup,
  HierarchyTopLabel,
  MoreButton,
  MoreCallout,
  MoreCalloutText,
  MoreCalloutTitle,
  MoreCharacterBlock,
  MoreInlineCode,
  MorePanel,
  MoreSubsection,
  MoreSubsectionText,
  MoreSubsectionTitle,
  MiniAfter,
  MiniArrow,
  MiniBefore,
  MiniDiagram,
  MiniDiagramColumn,
  MiniDiagramLabel,
  MiniNode,
  SectionDescription,
  SectionDescriptionEmphasis,
  SectionHeading,
  SectionNumber,
  SectionTitle,
  VisualLabel,
} from './ImportModal.styles';
import ImportReportingGroupsEffectHelpContent from './ImportReportingGroupsEffectHelpContent';
import CloseMark from '@/assets/icons/closeMark.svg?react';

export type ReportingGroupsHelpVariant = 'adaptPulse' | 'effect';

interface IImportReportingGroupsHelpModal {
  active: boolean;
  onClose: () => void;
  variant?: ReportingGroupsHelpVariant;
}

type ExampleRow = {
  id: string;
  values: [string, string?, string?];
  path: string[];
};

const ImportReportingGroupsHelpModal: FC<IImportReportingGroupsHelpModal> = ({
  active,
  onClose,
  variant = 'adaptPulse',
}) => {
  const { t } = useTranslation();
  const isEffect = variant === 'effect';
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);

  const exampleRows = useMemo<ExampleRow[]>(
    () => [
      {
        id: 'engineering-platform',
        values: [
          t('imports.reportingGroupsHelp.examples.engineering'),
          t('imports.reportingGroupsHelp.examples.backend'),
          t('imports.reportingGroupsHelp.examples.platform'),
        ],
        path: ['engineering', 'backend', 'platform'],
      },
      {
        id: 'engineering-payments',
        values: [
          t('imports.reportingGroupsHelp.examples.engineering'),
          t('imports.reportingGroupsHelp.examples.backend'),
          t('imports.reportingGroupsHelp.examples.payments'),
        ],
        path: ['engineering', 'backend', 'payments'],
      },
      {
        id: 'engineering-frontend',
        values: [
          t('imports.reportingGroupsHelp.examples.engineering'),
          t('imports.reportingGroupsHelp.examples.frontend'),
        ],
        path: ['engineering', 'frontend'],
      },
      {
        id: 'sales-nordics',
        values: [
          t('imports.reportingGroupsHelp.examples.sales'),
          t('imports.reportingGroupsHelp.examples.nordics'),
        ],
        path: ['sales', 'nordics'],
      },
      {
        id: 'sales-dach',
        values: [
          t('imports.reportingGroupsHelp.examples.sales'),
          t('imports.reportingGroupsHelp.examples.dach'),
        ],
        path: ['sales', 'dach'],
      },
      {
        id: 'marketing',
        values: [t('imports.reportingGroupsHelp.examples.marketing')],
        path: ['marketing'],
      },
    ],
    [t]
  );

  const activeRows = useMemo(() => {
    if (!activeItem) return new Set<string>();

    const hoveredRow = exampleRows.find(row => row.id === activeItem);
    if (hoveredRow) return new Set([hoveredRow.id]);

    return new Set(exampleRows.filter(row => row.path.includes(activeItem)).map(row => row.id));
  }, [activeItem, exampleRows]);

  const activePath = useMemo(() => {
    if (!activeItem) return [];
    const hoveredRow = exampleRows.find(row => row.id === activeItem);
    if (hoveredRow) return hoveredRow.path;

    const matchingPath = exampleRows.find(row => row.path.includes(activeItem))?.path;
    if (!matchingPath) return [activeItem];

    return matchingPath.slice(0, matchingPath.indexOf(activeItem) + 1);
  }, [activeItem, exampleRows]);

  const isActive = (node: string) => activePath.includes(node);
  const hasActiveDescendant = (node: string) =>
    exampleRows.some(row => activeRows.has(row.id) && row.path.includes(node));

  const activate = (id: string) => ({
    onMouseEnter: () => setActiveItem(id),
    onMouseLeave: () => setActiveItem(null),
    onFocus: () => setActiveItem(id),
    onBlur: () => setActiveItem(null),
  });

  if (!active) return null;

  return (
    <>
      <GuideBackdrop onClick={onClose} />
      <GuideModal
        role="dialog"
        aria-modal="true"
        aria-labelledby="reporting-groups-guide-title"
        data-testid="import-reporting-groups-help-content"
      >
        <GuideHeader>
          <GuideKickerRow>
            <GuideKickerLabel>
              {t(
                isEffect
                  ? 'imports.reportingGroupsHelp.effect.kicker'
                  : 'imports.reportingGroupsHelp.kicker'
              )}
            </GuideKickerLabel>
            <GuideSurveyBadge>
              {t(
                isEffect
                  ? 'imports.reportingGroupsHelp.effect.badge'
                  : 'imports.reportingGroupsHelp.badge'
              )}
            </GuideSurveyBadge>
          </GuideKickerRow>
          <GuideCloseButton type="button" aria-label={t('imports.close')} onClick={onClose}>
            <CloseMark />
          </GuideCloseButton>
          <GuideTitle id="reporting-groups-guide-title">
            {t('imports.reportingGroupsHelp.guideTitle')}
          </GuideTitle>
          <GuideSubtitle>
            {t(
              isEffect
                ? 'imports.reportingGroupsHelp.effect.guideSubtitle'
                : 'imports.reportingGroupsHelp.guideSubtitle'
            )}
          </GuideSubtitle>
        </GuideHeader>

        <GuideBody>
          {isEffect ? (
            <ImportReportingGroupsEffectHelpContent />
          ) : (
            <>
              <GuideSection>
                <SectionHeading>
                  <SectionNumber>1</SectionNumber>
                  <SectionTitle>{t('imports.reportingGroupsHelp.section1.title')}</SectionTitle>
                </SectionHeading>
                <SectionDescription>
                  {t('imports.reportingGroupsHelp.section1.description')}
                </SectionDescription>

                <VisualLabel>
                  {t('imports.reportingGroupsHelp.section1.visualLabelStructure')}
                </VisualLabel>
                <HierarchyCanvas onMouseLeave={() => setActiveItem(null)}>
                  <HierarchyTopGroup
                    $active={hasActiveDescendant('engineering')}
                    $direct={activeItem === 'engineering'}
                    {...activate('engineering')}
                  >
                    <HierarchyTopLabel>
                      {t('imports.reportingGroupsHelp.examples.engineering')}
                    </HierarchyTopLabel>
                    <HierarchyChildGroup
                      $active={isActive('backend')}
                      $contextActive={hasActiveDescendant('engineering')}
                      $parentDirect={activeItem === 'engineering'}
                      {...activate('backend')}
                    >
                      <span>{t('imports.reportingGroupsHelp.examples.backend')}</span>
                      <div>
                        <HierarchyLeaf
                          $active={isActive('platform')}
                          $contextActive={hasActiveDescendant('engineering')}
                          $parentDirect={activeItem === 'engineering'}
                          {...activate('platform')}
                        >
                          {t('imports.reportingGroupsHelp.examples.platform')}
                        </HierarchyLeaf>
                        <HierarchyLeaf
                          $active={isActive('payments')}
                          $contextActive={hasActiveDescendant('engineering')}
                          $parentDirect={activeItem === 'engineering'}
                          {...activate('payments')}
                        >
                          {t('imports.reportingGroupsHelp.examples.payments')}
                        </HierarchyLeaf>
                      </div>
                    </HierarchyChildGroup>
                    <HierarchyLeaf
                      $active={isActive('frontend')}
                      $contextActive={hasActiveDescendant('engineering')}
                      $parentDirect={activeItem === 'engineering'}
                      {...activate('frontend')}
                    >
                      {t('imports.reportingGroupsHelp.examples.frontend')}
                    </HierarchyLeaf>
                  </HierarchyTopGroup>

                  <HierarchyTopGroup
                    $active={hasActiveDescendant('sales')}
                    $direct={activeItem === 'sales'}
                    {...activate('sales')}
                  >
                    <HierarchyTopLabel>
                      {t('imports.reportingGroupsHelp.examples.sales')}
                    </HierarchyTopLabel>
                    <div>
                      <HierarchyLeaf
                        $active={isActive('nordics')}
                        $contextActive={hasActiveDescendant('sales')}
                        $parentDirect={activeItem === 'sales'}
                        {...activate('nordics')}
                      >
                        {t('imports.reportingGroupsHelp.examples.nordics')}
                      </HierarchyLeaf>
                      <HierarchyLeaf
                        $active={isActive('dach')}
                        $contextActive={hasActiveDescendant('sales')}
                        $parentDirect={activeItem === 'sales'}
                        {...activate('dach')}
                      >
                        {t('imports.reportingGroupsHelp.examples.dach')}
                      </HierarchyLeaf>
                    </div>
                  </HierarchyTopGroup>

                  <HierarchyLeaf
                    $standalone
                    $active={isActive('marketing')}
                    $contextActive={false}
                    $parentDirect={false}
                    {...activate('marketing')}
                  >
                    {t('imports.reportingGroupsHelp.examples.marketing')}
                  </HierarchyLeaf>
                </HierarchyCanvas>

                <VisualLabel>
                  {t('imports.reportingGroupsHelp.section1.visualLabelExcel')}
                </VisualLabel>
                <ExampleTable>
                  <thead>
                    <ExampleTableRow>
                      {[1, 2, 3].map(number => (
                        <ExampleTableHead key={number}>
                          {t('imports.reportingGroupsHelp.section1.departmentColumn', { number })}
                        </ExampleTableHead>
                      ))}
                    </ExampleTableRow>
                  </thead>
                  <tbody>
                    {exampleRows.map(row => (
                      <ExampleTableRow
                        key={row.id}
                        $active={activeRows.has(row.id)}
                        tabIndex={0}
                        {...activate(row.id)}
                      >
                        {Array.from({ length: 3 }, (_, index) => row.values[index]).map(
                          (value, index) => (
                            <ExampleTableCell key={`${row.id}-${index}`}>
                              {value || <span>{t('imports.reportingGroupsHelp.emptyCell')}</span>}
                            </ExampleTableCell>
                          )
                        )}
                      </ExampleTableRow>
                    ))}
                  </tbody>
                </ExampleTable>
              </GuideSection>

              <GuideSection>
                <SectionHeading>
                  <SectionNumber>2</SectionNumber>
                  <SectionTitle>{t('imports.reportingGroupsHelp.section2.title')}</SectionTitle>
                </SectionHeading>
                <SectionDescription>
                  <Trans
                    i18nKey="imports.reportingGroupsHelp.section2.description"
                    components={{ strong: <SectionDescriptionEmphasis /> }}
                  />
                </SectionDescription>
              </GuideSection>

              <GuideSection>
                <SectionHeading>
                  <SectionNumber>3</SectionNumber>
                  <SectionTitle>{t('imports.reportingGroupsHelp.section3.title')}</SectionTitle>
                </SectionHeading>
                <SectionDescription>
                  {t('imports.reportingGroupsHelp.section3.description')}
                </SectionDescription>

                <MiniDiagram>
                  <MiniDiagramColumn>
                    <MiniDiagramLabel>
                      {t('imports.reportingGroupsHelp.section3.inYourFile')}
                    </MiniDiagramLabel>
                    <MiniBefore>
                      <strong>{t('imports.reportingGroupsHelp.examples.engineering')}</strong>
                      <div>
                        <MiniNode>{t('imports.reportingGroupsHelp.examples.backend')}</MiniNode>
                        <MiniNode>{t('imports.reportingGroupsHelp.examples.frontend')}</MiniNode>
                      </div>
                    </MiniBefore>
                  </MiniDiagramColumn>
                  <MiniArrow>→</MiniArrow>
                  <MiniDiagramColumn>
                    <MiniDiagramLabel>
                      {t('imports.reportingGroupsHelp.section3.afterImport')}
                    </MiniDiagramLabel>
                    <MiniAfter>
                      <strong>{t('imports.reportingGroupsHelp.section3.groupLevelLabel')}</strong>
                      <div>
                        <MiniNode>{t('imports.reportingGroupsHelp.examples.engineering')}</MiniNode>
                        <MiniNode>{t('imports.reportingGroupsHelp.examples.backend')}</MiniNode>
                        <MiniNode>{t('imports.reportingGroupsHelp.examples.frontend')}</MiniNode>
                      </div>
                    </MiniAfter>
                  </MiniDiagramColumn>
                </MiniDiagram>

                <MoreButton
                  type="button"
                  aria-expanded={showMore}
                  onClick={() => setShowMore(value => !value)}
                >
                  <span>{showMore ? '−' : '+'}</span>
                  {showMore
                    ? t('imports.reportingGroupsHelp.hide')
                    : t('imports.reportingGroupsHelp.showMore')}
                </MoreButton>
                {showMore ? (
                  <MorePanel>
                    <MoreSubsection>
                      <MoreSubsectionTitle>
                        {t('imports.reportingGroupsHelp.more.columnNamingTitle')}
                      </MoreSubsectionTitle>
                      <MoreSubsectionText>
                        <Trans
                          i18nKey="imports.reportingGroupsHelp.more.columnNamingText"
                          components={[
                            <MoreInlineCode key="department1" />,
                            <MoreInlineCode key="department2" />,
                            <MoreInlineCode key="department3" />,
                          ]}
                        />
                      </MoreSubsectionText>
                    </MoreSubsection>

                    <MoreSubsection>
                      <MoreSubsectionTitle>
                        {t('imports.reportingGroupsHelp.more.charactersTitle')}
                      </MoreSubsectionTitle>
                      <MoreSubsectionText>
                        {t('imports.reportingGroupsHelp.more.charactersText')}
                      </MoreSubsectionText>
                      <MoreCharacterBlock>
                        {t('imports.reportingGroupsHelp.charactersBlock')}
                      </MoreCharacterBlock>
                    </MoreSubsection>

                    <MoreCallout>
                      <MoreCalloutTitle>
                        {t('imports.reportingGroupsHelp.more.safeReuploadTitle')}
                      </MoreCalloutTitle>
                      <MoreCalloutText>
                        {t('imports.reportingGroupsHelp.more.safeReuploadText')}
                      </MoreCalloutText>
                    </MoreCallout>
                  </MorePanel>
                ) : null}
              </GuideSection>
            </>
          )}
        </GuideBody>

        <GuideFooter>
          <GuidePrimaryButton type="button" onClick={onClose}>
            {t('imports.reportingGroupsHelp.gotIt')}
          </GuidePrimaryButton>
        </GuideFooter>
      </GuideModal>
    </>
  );
};

export default ImportReportingGroupsHelpModal;
